import { analyticsUrl } from "@dashboard/analytics/urls";
import { attributeListUrl } from "@dashboard/attributes/urls";
import { useIsSuperAdmin } from "@dashboard/auth/hooks/useIsSuperAdmin";
import { useUser } from "@dashboard/auth/useUser";
import { categoryListUrl } from "@dashboard/categories/urls";
import { channelsListUrl } from "@dashboard/channels/urls";
import { configurationMenuUrl } from "@dashboard/configuration/urls";
// import { SidebarAppAlert } from "@dashboard/extensions/components/AppAlerts/SidebarAppAlert";
// import { useAppsAlert } from "@dashboard/extensions/components/AppAlerts/useAppsAlert";
import { extensionMountPoints } from "@dashboard/extensions/extensionMountPoints";
import { useExtensions } from "@dashboard/extensions/hooks/useExtensions";
// import {
//   extensionsAppSection,
//   extensionsCustomSection,
//   ExtensionsPaths,
//   extensionsPluginSection,
// } from "@dashboard/extensions/urls";
import { PermissionEnum } from "@dashboard/graphql";
import ChannelsIcon from "@dashboard/icons/Channels";
import { ConfigurationIcon } from "@dashboard/icons/Configuration";
import { CustomersIcon } from "@dashboard/icons/Customers";
import { HomeIcon } from "@dashboard/icons/Home";
// import { MarketplaceIcon } from "@dashboard/icons/Marketplace";
import { OrdersIcon } from "@dashboard/icons/Orders";
import { ProductsIcon } from "@dashboard/icons/Products";
import { sectionNames } from "@dashboard/intl";
import {
  learnDiscoveryListPath,
  learnDiscoveryPath,
  learnDiscoverySettingsPath,
} from "@dashboard/learnDiscovery/urls";
import { multiVendorRequestsListPath } from "@dashboard/multiVendorRequests/urls";
import { orderListUrl } from "@dashboard/orders/urls";
import { productListUrl } from "@dashboard/products/urls";
import { productTypeListUrl } from "@dashboard/productTypes/urls";
import { sellerPayoutStatementsUrl } from "@dashboard/sellerPayoutStatements/urls";
import { specialRequestsListUrl } from "@dashboard/specialRequests/urls";
import { staffListUrl } from "@dashboard/staff/urls";
import { MANAGE_SUBSCRIPTION_PERMISSION } from "@dashboard/subscriptions/permissions";
import { planConfigurationUrl } from "@dashboard/subscriptions/urls";
import { userInterestsPath } from "@dashboard/userInterest/urls";
import { Box } from "@saleor/macaw-ui-next";
import { BarChart3, BookOpen, ClipboardList, CreditCard, ReceiptText, Users } from "lucide-react";
import React from "react";
import { defineMessages, useIntl } from "react-intl";

import { SidebarMenuItem } from "../types";
import { mapToExtensionsItems } from "../utils";

const messages = defineMessages({
  productAttributes: {
    id: "TOdc60",
    defaultMessage: "Product Attributes",
  },
});

export function useMenuStructure(): SidebarMenuItem[] {
  // const { handleAppsListItemClick, hasNewFailedAttempts } = useAppsAlert();

  const extensions = useExtensions(extensionMountPoints.NAVIGATION_SIDEBAR);
  const intl = useIntl();
  const { user } = useUser();
  const isSuperAdmin = useIsSuperAdmin();

  const appExtensionsHeaderItem: SidebarMenuItem = {
    id: "extensions",
    label: intl.formatMessage(sectionNames.appExtensions),
    type: "divider",
    paddingY: 1.5,
  };

  // const getExtensionsSection = (): SidebarMenuItem => ({
  //   icon: renderIcon(<MarketplaceIcon />),
  //   label: intl.formatMessage(sectionNames.extensions),
  //   permissions: [],
  //   id: "installed-extensions",
  //   url: ExtensionsPaths.installedExtensions,
  //   type: "itemGroup",
  //   endAdornment: <SidebarAppAlert hasNewFailedAttempts={hasNewFailedAttempts} />,
  //   onClick: () => handleAppsListItemClick(new Date().toISOString()),
  //   children: [
  //     {
  //       label: (
  //         <Box display="flex" alignItems="center" gap={3}>
  //           {intl.formatMessage(sectionNames.installedExtensions)}
  //           <SidebarAppAlert hasNewFailedAttempts={hasNewFailedAttempts} small />
  //         </Box>
  //       ),
  //       id: "installed-extensions",
  //       url: ExtensionsPaths.installedExtensions,
  //       matchUrls: [
  //         ExtensionsPaths.installedExtensions,
  //         extensionsCustomSection,
  //         extensionsAppSection,
  //         extensionsPluginSection,
  //       ],
  //       permissions: [],
  //       type: "item",
  //     },
  //     {
  //       label: intl.formatMessage(sectionNames.exploreExtensions),
  //       id: "explore-extensions",
  //       url: ExtensionsPaths.exploreExtensions,
  //       permissions: [],
  //       type: "item",
  //     },
  //   ],
  // });

  const menuItems: SidebarMenuItem[] = [
    {
      icon: renderIcon(<HomeIcon />),
      label: intl.formatMessage(sectionNames.home),
      id: "home",
      url: "/",
      type: "item",
    },
    {
      icon: renderIcon(<BarChart3 />),
      label: intl.formatMessage(sectionNames.analytics),
      id: "analytics",
      url: analyticsUrl(),
      permissions: [],
      type: "item",
    },
    // {
    //   icon: renderIcon(<Search size={iconSize.small} strokeWidth={2.4} />),
    //   label: (
    //     <Box display="flex" alignItems="center" gap={2}>
    //       {intl.formatMessage(sectionNames.search)}
    //       <SearchShortcut />
    //     </Box>
    //   ),
    //   id: "search",
    //   url: "/search",
    //   permissions: [
    //     PermissionEnum.MANAGE_PRODUCTS,
    //     PermissionEnum.MANAGE_PAGES,
    //     PermissionEnum.MANAGE_PAGE_TYPES_AND_ATTRIBUTES,
    //     PermissionEnum.MANAGE_ORDERS,
    //   ],
    //   type: "item",
    // },
    {
      children: [
        {
          label: intl.formatMessage(sectionNames.products),
          id: "products",
          url: productListUrl(),
          permissions: [PermissionEnum.MANAGE_PRODUCTS],
          type: "item",
        },
        {
          label: intl.formatMessage(sectionNames.categories),
          id: "categories",
          url: categoryListUrl(),
          permissions: [PermissionEnum.MANAGE_PRODUCTS],
          type: "item",
        },
        {
          label: intl.formatMessage(messages.productAttributes),
          id: "attributes",
          url: attributeListUrl({ type: "PRODUCT_TYPE" }),
          permissions: [PermissionEnum.MANAGE_PRODUCT_TYPES_AND_ATTRIBUTES],
          type: "item",
        },
        // Hidden: Collections submenu
        // {
        //   label: intl.formatMessage(sectionNames.collections),
        //   id: "collections",
        //   url: collectionListUrl(),
        //   permissions: [PermissionEnum.MANAGE_PRODUCTS],
        //   type: "item",
        // },
        // Hidden: Gift Cards submenu
        // {
        //   label: intl.formatMessage(sectionNames.giftCards),
        //   id: "giftCards",
        //   url: giftCardListUrl(),
        //   permissions: [PermissionEnum.MANAGE_GIFT_CARD],
        //   type: "item",
        // },
        {
          label: intl.formatMessage(sectionNames.industryTypes),
          id: "industry-types",
          url: productTypeListUrl(),
          permissions: [PermissionEnum.MANAGE_PRODUCT_TYPES_AND_ATTRIBUTES],
          type: "item",
        },
        ...mapToExtensionsItems(extensions.NAVIGATION_CATALOG, appExtensionsHeaderItem),
      ],
      icon: renderIcon(<ProductsIcon />),
      url: productListUrl(),
      label: intl.formatMessage(sectionNames.catalog),
      permissions: [
        PermissionEnum.MANAGE_PRODUCTS,
        PermissionEnum.MANAGE_PRODUCT_TYPES_AND_ATTRIBUTES,
      ],
      id: "products",
      type: "itemGroup",
    },
    {
      children: [
        {
          label: intl.formatMessage(sectionNames.orders),
          id: "orders-list",
          url: orderListUrl(),
          permissions: [PermissionEnum.MANAGE_ORDERS],
          type: "item",
        },
        {
          label: intl.formatMessage(sectionNames.multiVendorRequests),
          id: "multi-vendor-requests",
          url: multiVendorRequestsListPath,
          permissions: [PermissionEnum.MANAGE_ORDERS],
          type: "item",
        },
        {
          label: intl.formatMessage(sectionNames.specialRequests),
          id: "special-requests",
          url: specialRequestsListUrl(),
          permissions: [PermissionEnum.MANAGE_ORDERS],
          type: "item",
        },
      ],
      icon: renderIcon(<OrdersIcon />),
      label: intl.formatMessage(sectionNames.orders),
      permissions: [PermissionEnum.MANAGE_ORDERS],
      id: "orders",
      url: orderListUrl(),
      type: "itemGroup",
    },
    {
      icon: renderIcon(<BookOpen />),
      label: intl.formatMessage(sectionNames.learnDiscovery),
      permissions: [PermissionEnum.MANAGE_PAGES],
      id: "learn-discovery",
      url: learnDiscoveryListPath,
      matchUrls: [learnDiscoveryListPath, learnDiscoverySettingsPath, learnDiscoveryPath(":id")],
      type: "item",
    },
    {
      icon: renderIcon(<CustomersIcon />),
      label: intl.formatMessage(sectionNames.userManagement),
      permissions: [PermissionEnum.MANAGE_USERS],
      id: "user-management",
      url: "/user-management/users",
      type: "item",
    },
    ...(isSuperAdmin
      ? [
          {
            icon: renderIcon(<ReceiptText />),
            label: intl.formatMessage(sectionNames.sellerPayoutStatements),
            id: "seller-payout-statements",
            url: sellerPayoutStatementsUrl(),
            permissions: [],
            type: "item" as const,
          },
        ]
      : []),
    {
      icon: renderIcon(<ClipboardList />),
      label: intl.formatMessage(sectionNames.userInterest),
      permissions: [PermissionEnum.MANAGE_USERS],
      id: "user-interest",
      url: userInterestsPath,
      type: "item",
    },
    {
      children: [
        {
          label: intl.formatMessage(sectionNames.planConfiguration),
          id: "plan-configuration",
          url: planConfigurationUrl,
          permissions: [MANAGE_SUBSCRIPTION_PERMISSION],
          type: "item",
        },
      ],
      icon: renderIcon(<CreditCard />),
      label: intl.formatMessage(sectionNames.subscriptions),
      permissions: [MANAGE_SUBSCRIPTION_PERMISSION],
      id: "subscriptions",
      type: "itemGroup",
      url: planConfigurationUrl,
    },
    {
      icon: renderIcon(<ChannelsIcon />),
      label: intl.formatMessage(sectionNames.channels),
      permissions: [PermissionEnum.MANAGE_CHANNELS],
      id: "channels",
      url: channelsListUrl(),
      type: "item",
    },
    {
      icon: renderIcon(<Users />),
      label: intl.formatMessage({
        defaultMessage: "Manage Staff",
        id: "E99Xy+",
      }),
      permissions: [PermissionEnum.MANAGE_STAFF],
      id: "manage-staff",
      url: staffListUrl(),
      type: "item",
    },
    {
      icon: renderIcon(<ConfigurationIcon />),
      label: intl.formatMessage(sectionNames.configuration),
      permissions: [PermissionEnum.MANAGE_PLUGINS],
      id: "configuration",
      url: configurationMenuUrl,
      type: "item",
    },
    // {
    //   children: [
    //     {
    //       label: intl.formatMessage(sectionNames.promotions),
    //       id: "promotions",
    //       url: saleListUrl(),
    //       type: "item",
    //     },
    //     {
    //       label: intl.formatMessage(sectionNames.vouchers),
    //       id: "vouchers",
    //       url: voucherListUrl(),
    //       type: "item",
    //     },
    //     ...mapToExtensionsItems(extensions.NAVIGATION_DISCOUNTS, appExtensionsHeaderItem),
    //   ],
    //   icon: renderIcon(<DiscountsIcon />),
    //   label: intl.formatMessage(commonMessages.discounts),
    //   permissions: [PermissionEnum.MANAGE_DISCOUNTS],
    //   url: saleListUrl(),
    //   id: "discounts",
    //   type: "itemGroup",
    // },
    // {
    //   children: [
    //     {
    //       label: intl.formatMessage(sectionNames.models),
    //       id: "models",
    //       url: pageListPath,
    //       permissions: [PermissionEnum.MANAGE_PAGES],
    //       type: "item",
    //     },
    //     {
    //       label: intl.formatMessage(sectionNames.modelTypes),
    //       id: "model-types",
    //       url: pageTypeListUrl(),
    //       permissions: [
    //         PermissionEnum.MANAGE_PAGES,
    //         PermissionEnum.MANAGE_PAGE_TYPES_AND_ATTRIBUTES,
    //       ],
    //       type: "item",
    //     },
    //     {
    //       label: intl.formatMessage(sectionNames.structures),
    //       id: "structures",
    //       url: menuListUrl(),
    //       permissions: [PermissionEnum.MANAGE_MENUS],
    //       type: "item",
    //     },
    //     ...mapToExtensionsItems(extensions.NAVIGATION_PAGES, appExtensionsHeaderItem),
    //   ],
    //   icon: renderIcon(<ModelingIcon />),
    //   label: intl.formatMessage(sectionNames.modeling),
    //   permissions: [PermissionEnum.MANAGE_PAGES, PermissionEnum.MANAGE_MENUS],
    //   id: "modeling",
    //   url: pageListPath,
    //   endAdornment: <Ripple model={ripplePagesAreModels} />,
    //   type: "itemGroup",
    // },
    // getExtensionsSection(),
  ];
  const isMenuItemPermitted = (menuItem: SidebarMenuItem): boolean => {
    const userPermissions = (user?.userPermissions || []).map(permission => permission.code);

    if (!menuItem?.permissions || menuItem?.permissions?.length < 1) {
      return true;
    }

    return menuItem.permissions.some(permission => userPermissions.includes(permission));
  };
  const getFilteredMenuItems = (menuItems: SidebarMenuItem[]): SidebarMenuItem[] =>
    menuItems.filter(isMenuItemPermitted);

  return menuItems.reduce((resultItems: SidebarMenuItem[], menuItem: SidebarMenuItem) => {
    if (!isMenuItemPermitted(menuItem)) {
      return resultItems;
    }

    const { children } = menuItem;
    const filteredChildren = children ? getFilteredMenuItems(children) : undefined;

    return [...resultItems, { ...menuItem, children: filteredChildren }];
  }, []);
}

function renderIcon(icon: React.ReactNode): JSX.Element {
  return (
    <Box color="default2" __width={20} __height={20}>
      {icon}
    </Box>
  );
}
